// =============
// Google Play Real-time Developer Notifications (RTDN)
// =============

import z from "zod";

/* =========================
 * Pub/Sub Push Envelope
 * ========================= */

/** The outer Pub/Sub push message envelope received at our endpoint. */
export const GooglePubSubMessageSchema = z.object({
	message: z.object({
		data: z.string(), // base64-encoded DeveloperNotification JSON
		messageId: z.string(),
		publishTime: z.string().optional(),
	}),
	subscription: z.string(),
});
export type GooglePubSubMessage = z.infer<typeof GooglePubSubMessageSchema>;

/* =========================
 * Subscription Notification
 * ========================= */

/**
 * Subscription notification from Google Play.
 *
 * notificationType values:
 *  1  SUBSCRIPTION_RECOVERED
 *  2  SUBSCRIPTION_RENEWED
 *  3  SUBSCRIPTION_CANCELED
 *  4  SUBSCRIPTION_PURCHASED
 *  5  SUBSCRIPTION_ON_HOLD
 *  6  SUBSCRIPTION_IN_GRACE_PERIOD
 *  7  SUBSCRIPTION_RESTARTED
 *  12 SUBSCRIPTION_REVOKED
 *  13 SUBSCRIPTION_EXPIRED
 */
export const GoogleSubscriptionNotificationSchema = z.object({
	version: z.string(),
	notificationType: z.coerce.number(),
	purchaseToken: z.string(),
	subscriptionId: z.string(),
});
export type GoogleSubscriptionNotification = z.infer<
	typeof GoogleSubscriptionNotificationSchema
>;

/* =========================
 * One-Time Product Notification
 * ========================= */

/**
 * One-time product notification from Google Play.
 *
 * notificationType values:
 *  1 ONE_TIME_PRODUCT_PURCHASED
 *  2 ONE_TIME_PRODUCT_CANCELED
 */
export const GoogleOneTimeProductNotificationSchema = z.object({
	version: z.string(),
	notificationType: z.coerce.number(),
	purchaseToken: z.string(),
	sku: z.string(),
});
export type GoogleOneTimeProductNotification = z.infer<
	typeof GoogleOneTimeProductNotificationSchema
>;

/* =========================
 * Voided Purchase Notification
 * ========================= */

/**
 * Voided purchase notification (refund).
 *
 * productType: 1 = Subscription, 2 = One-time product
 * refundType:  1 = Full refund, 2 = Quantity-based partial refund
 */
export const GoogleVoidedPurchaseNotificationSchema = z.object({
	purchaseToken: z.string(),
	orderId: z.string(),
	productType: z.coerce.number(),
	refundType: z.coerce.number().optional(),
});
export type GoogleVoidedPurchaseNotification = z.infer<
	typeof GoogleVoidedPurchaseNotificationSchema
>;

/* =========================
 * Test Notification
 * ========================= */

export const GoogleTestNotificationSchema = z.object({
	version: z.string(),
});

/* =========================
 * Developer Notification (decoded from base64 message.data)
 * ========================= */

export const GoogleDeveloperNotificationSchema = z.object({
	version: z.string(),
	packageName: z.string(),
	eventTimeMillis: z.coerce.string(), // string-encoded long
	subscriptionNotification: GoogleSubscriptionNotificationSchema.optional(),
	oneTimeProductNotification: GoogleOneTimeProductNotificationSchema.optional(),
	voidedPurchaseNotification: GoogleVoidedPurchaseNotificationSchema.optional(),
	testNotification: GoogleTestNotificationSchema.optional(),
});
export type GoogleDeveloperNotification = z.infer<
	typeof GoogleDeveloperNotificationSchema
>;

/* =========================
 * Queue Message (enqueued for async processing)
 * ========================= */

export const GoogleRtdnQueueMessageSchema = z.object({
	appId: z.string(),
	messageId: z.string(),
	notification: GoogleDeveloperNotificationSchema,
	receivedAt: z.string(), // ISO timestamp
});
export type GoogleRtdnQueueMessage = z.infer<
	typeof GoogleRtdnQueueMessageSchema
>;

/* =========================
 * Google Play Developer API
 * ========================= */

/** Price object from Google Play API (units + nanos representation). */
export const GoogleMoneySchema = z.object({
	currencyCode: z.string(),
	units: z.string().optional(),
	nanos: z.number().optional(),
});
export type GoogleMoney = z.infer<typeof GoogleMoneySchema>;

/** Service account JSON key (from Google Cloud Console). */
export const GoogleServiceAccountSchema = z.object({
	client_email: z.string(),
	private_key: z.string(),
});

/** OAuth2 token exchange response. */
export const GoogleTokenResponseSchema = z.object({
	access_token: z.string(),
	expires_in: z.number(),
});

/** Subscription purchase details from subscriptionsv2.get API. */
export const SubscriptionPurchaseV2Schema = z.object({
	kind: z.string(),
	regionCode: z.string(),
	lineItems: z.array(
		z.object({
			productId: z.string(),
			expiryTime: z.string(),
			autoRenewingPlan: z
				.object({
					autoRenewEnabled: z.boolean(),
					recurringPrice: GoogleMoneySchema.optional(),
				})
				.optional(),
			prepaidPlan: z.record(z.string(), z.unknown()).optional(),
			offerDetails: z
				.object({
					basePlanId: z.string(),
					offerId: z.string().optional(),
					offerTags: z.array(z.string()).optional(),
				})
				.optional(),
		}),
	),
	startTime: z.string(),
	subscriptionState: z.string(),
	latestOrderId: z.string(),
	acknowledgementState: z.string(),
	externalAccountIdentifiers: z
		.object({
			externalAccountId: z.string().optional(),
			obfuscatedExternalAccountId: z.string().optional(),
			obfuscatedExternalProfileId: z.string().optional(),
		})
		.optional(),
});
export type SubscriptionPurchaseV2 = z.infer<
	typeof SubscriptionPurchaseV2Schema
>;
